import { Component, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { SimpleModalComponent } from "ngx-simple-modal";
import * as _ from "lodash";
import { OrderLine } from "../../../../pb-screen/models/order-line/orderLine.interface";
import { PlanningBlock } from "../../../../pb-screen/models/planning-block/planning-block.interface";
import { TableStateService } from "../../../../services/table.state.service";
import { UserFeedbackService } from "../../../../services/user-feedback.service";
import {
  ContainerReuseBusinessResponse,
  ContainerReuseInfo,
  ContainerReusePreviewOrder,
  ContainerReusePreviewOrderLine,
  ContainerReusePreviewRequest,
  ContainerReusePreviewResponse,
  ContainerReusePreviewResultLine,
  RequestContainerReuseResponse,
} from "../../../models/container-reuse/container.reuse.interface";
import {
  RE_USED,
  TO_BE_PLANNED,
} from "../../../models/status-type.interface";
import { formatErrorMessage, UNEXPECTED_SERVER_ERROR_KEY } from "../../../services/error-message-formatter";
import { SharedService } from "../../../services/shared.service";
import { SharedActions } from "../../../shared.actions";
import { ContainerReuseStatus } from "../container-reuse-modal/container-reuse.state.enum";

export interface ContainerReuseNextModalInput {
  selectedPlanningBlocks?: PlanningBlock[];
  reuseNumber?: string;
}

export interface ReusePreviewOrderLineRow {
  orderNo: string;
  orderLineId: string;
  lineSequence: number;
  actionCode: string;
  status: string;
  planningBlockStatusType: number;
  addressNo: string;
  address: string;
  displayAddress: string;
  planningBlockId: number;
  sortingKey: number;
}

export interface ReusePreviewResultRow extends ContainerReusePreviewResultLine {
  orderNo: string;
  addressNo: string;
  address: string;
  displayLineSequence: number | null;
  displayAddress: string;
  displayAddressWithNumber: string;
  displayOrderLineId: string;
  displayPlanningBlockId: string;
  isNewPlanningBlockLine: boolean;
  planningBlockStatusType: number;
  startsPlanningBlock: boolean;
}

export interface ReuseActionState {
  disabled: boolean;
  tooltipKey: string;
  buttonClass: string;
}

@Component({
  selector: "container-reuse-next-modal",
  templateUrl: "./container-reuse-next-modal.component.html",
  styleUrls: ["./container-reuse-next-modal.component.scss"],
})
export class ContainerReuseNextComponent
  extends SimpleModalComponent<ContainerReuseNextModalInput, void>
  implements ContainerReuseNextModalInput, OnInit {
  public selectedPlanningBlocks: PlanningBlock[] = [];
  public reuseNumber: string = "";
  public loadingSpinner: boolean = false;

  public reuseNumberDisplay: string = "";
  public validationErrorMessage: string = "";
  public validationWarningMessage: string = "";
  public planningBlocksLoadedByReuseNumber: boolean = false;
  public containerReuseInfo: ContainerReuseInfo = null;
  public containerReuseInfoStatus: number = -1;
  public containerReuseInfoStatusText: string = "";
  public selectedContainerReuseInfoStatus: number = ContainerReuseStatus.REQUESTED;
  public isReused: boolean = false;
  public importOrderLines: ReusePreviewOrderLineRow[] = [];
  public exportOrderLines: ReusePreviewOrderLineRow[] = [];
  public previewResultRows: ReusePreviewResultRow[] = [];
  public previewResponse: ContainerReusePreviewResponse = null;
  public previewErrorMessage: string = "";
  public previewLoaded: boolean = false;
  public showSelectionErrorInOrderSections: boolean = false;

  public importPlanningBlocks: PlanningBlock[] = [];
  public exportPlanningBlocks: PlanningBlock[] = [];
  public invalidPlanningBlocks: PlanningBlock[] = [];
  public sortedPlanningBlocks: PlanningBlock[] = [];
  private readonly planningBlockById = new Map<number, PlanningBlock>();

  constructor(
    private readonly sharedActions: SharedActions,
    private readonly sharedService: SharedService,
    private readonly translate: TranslateService,
    private readonly userFeedbackService: UserFeedbackService,
    private readonly tableStateService: TableStateService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.reuseNumber = this.reuseNumber || "";
    this.reuseNumberDisplay = this.reuseNumber;
    this.selectedPlanningBlocks = this.normalizePlanningBlocks(this.selectedPlanningBlocks || []);

    if (this.reuseNumber) {
      this.loadPlanningBlocksByReuseNumber();
      return;
    }

    this.classifyPlanningBlocks();
    this.reuseNumberDisplay = this.resolveReuseNumberFromPlanningBlocks();
    this.validateInput();

    if (this.reuseNumberDisplay) {
      this.loadContainerReuseInfo(this.reuseNumberDisplay);
    }

    this.loadReusePreview();
  }

  public async onConfirmClicked(): Promise<void> {
    if (this.isConfirmDisabled()) {
      return;
    }

    let completed = false;

    switch (this.selectedContainerReuseInfoStatus) {
      case ContainerReuseStatus.REQUEST_AND_APPROVE:
        completed = await this.requestAndApproveContainerReuse();
        break;
      case ContainerReuseStatus.REQUESTED:
        completed = await this.requestContainerReuse();
        break;
      case ContainerReuseStatus.APPROVED:
        completed = await this.updateContainerReuseStateToApproved(this.reuseNumberDisplay);
        break;
      case ContainerReuseStatus.REJECTED:
        completed = await this.updateContainerReuseStateToRejected(this.reuseNumberDisplay);
        break;
      default:
        break;
    }

    if (completed) {
      this.close();
    }
  }

  public async onReuseActionClicked(status: number): Promise<void> {
    if (this.isReuseActionButtonDisabled(status)) {
      return;
    }

    this.selectedContainerReuseInfoStatus = status;
    await this.onConfirmClicked();
  }

  public isConfirmDisabled(): boolean {
    return this.getReuseActionState(this.selectedContainerReuseInfoStatus).disabled;
  }

  public isReuseActionButtonDisabled(status: number): boolean {
    return this.getReuseActionState(status).disabled;
  }

  public isReuseStatusOptionDisabled(status: number): boolean {
    return this.getReuseActionState(status).disabled;
  }

  public getReuseActionState(status: number): ReuseActionState {
    const tooltipKey = this.resolveReuseActionDisabledTooltipKey(status);

    return {
      disabled: !!tooltipKey,
      tooltipKey,
      buttonClass: tooltipKey ? "btn-default container-reuse-action-disabled" : "btn-primary",
    };
  }

  public enableUi(): void {
    this.resetModalState();
    this.sharedActions.setUiState(true);
    this.close();
  }

  public getCarrierNo(planningBlock: PlanningBlock): string {
    return (planningBlock as any).carrierNo || planningBlock.carrierId || "";
  }

  public isCarrierNoMismatch(): boolean {
    return this.hasValidImportExportPair()
      && this.getCarrierNo(this.importPlanningBlocks[0]) !== this.getCarrierNo(this.exportPlanningBlocks[0]);
  }

  public isContainerOrTrailerTypeMismatch(): boolean {
    return this.hasValidImportExportPair()
      && this.importPlanningBlocks[0].containerOrTrailerType !== this.exportPlanningBlocks[0].containerOrTrailerType;
  }

  public formatTractionOrder(planningBlock: PlanningBlock): string {
    switch (planningBlock.tractionOrder) {
      case 1:
        return `1 - ${this.translate.instant("CONTAINER_REUSE.IMPORT_ABBREVIATION")}`;
      case 2:
        return `2 - ${this.translate.instant("CONTAINER_REUSE.EXPORT_ABBREVIATION")}`;
      default:
        return `${planningBlock.tractionOrder} - ${this.translate.instant("CONTAINER_REUSE.INVALID_ABBREVIATION")}`;
    }
  }

  public isInvalidTractionOrder(planningBlock: PlanningBlock): boolean {
    return planningBlock.tractionOrder !== 1 && planningBlock.tractionOrder !== 2;
  }

  public shouldShowValidationErrorMessage(): boolean {
    return !!this.validationErrorMessage && !this.showSelectionErrorInOrderSections;
  }

  public shouldShowPreviewErrorMessage(): boolean {
    return !!this.previewErrorMessage && !this.hasSuccessfulReuse();
  }

  public shouldShowReusePreview(): boolean {
    return this.previewLoaded && !this.previewErrorMessage && !this.hasSuccessfulReuse();
  }

  public hasSuccessfulReuse(): boolean {
    return this.containerReuseInfoStatus === ContainerReuseStatus.APPROVED
      || this.isReused
      || Array.from(this.planningBlockById.values())
        .some((planningBlock) => this.hasReusedPlanningBlockStatus(planningBlock));
  }

  public shouldShowOrderSections(): boolean {
    return this.selectedPlanningBlocks.length > 0 || this.importOrderLines.length > 0 || this.exportOrderLines.length > 0;
  }

  public getImportPlanningBlockForSection(): PlanningBlock {
    return this.hasValidOrderSectionSelection() ? this.importPlanningBlocks[0] : null;
  }

  public getExportPlanningBlockForSection(): PlanningBlock {
    return this.hasValidOrderSectionSelection() ? this.exportPlanningBlocks[0] : null;
  }

  public isImportReusePlanningBlockLine(row: ReusePreviewOrderLineRow): boolean {
    return !!row && row.planningBlockId === this.resolveReusePlanningBlockId(this.importOrderLines, true);
  }

  public isExportReusePlanningBlockLine(row: ReusePreviewOrderLineRow): boolean {
    return !!row && row.planningBlockId === this.resolveReusePlanningBlockId(this.exportOrderLines, false);
  }

  public translateContainerReuseInfoStatusToText(): void {
    const translateString: string = this.getContainerStatusTranslatedString();

    if (translateString) {
      this.translate.get(translateString).subscribe((res: string) => {
        this.containerReuseInfoStatusText = res;
      });
    }
  }

  public getContainerStatusTranslatedString(): string {
    switch (this.containerReuseInfoStatus) {
      case ContainerReuseStatus.APPROVED:
        return "CONTAINER_REUSE.APPROVE";
      case ContainerReuseStatus.REQUESTED:
        return "CONTAINER_REUSE.REQUEST";
      case ContainerReuseStatus.REJECTED:
        return "CONTAINER_REUSE.REJECT";
      case ContainerReuseStatus.CANCELED:
        return "CONTAINER_REUSE.CANCEL";
      default:
        return "";
    }
  }

  public async updateContainerReuseStateToRejected(reuseNo: string): Promise<boolean> {
    this.loadingSpinner = true;
    return this.updateContainerReuseStateToRejectedServiceCall(reuseNo);
  }

  public async updateContainerReuseStateToRejectedServiceCall(reuseNo: string): Promise<boolean> {
    const planningBlockIds = this.resolveCurrentPlanningBlockIds();
    try {
      const response = await this.sharedService.rejectContainerReuseRequest(reuseNo).toPromise();
      this.ensureReuseResponseSuccess(response, "TOASTR_MESSAGES.ERROR.CONTAINER_REUSE_REQUEST_APPROVE_APPROVE_FAILED");
      await this.refreshPlanningBlocksForReuse(reuseNo, planningBlockIds);
      const res = await this.translate.get("TOASTR_MESSAGES.SUCCESS.CONTAINER_REUSE_REJECTED_COMPLETED_SUCCESSFULLY").toPromise();
      this.onSuccessEvents(res);
      return true;
    } catch (error) {
      await this.handleReuseActionError(error);
      return false;
    } finally {
      this.loadingSpinner = false;
    }
  }

  public async updateContainerReuseStateToApproved(reuseNo: string): Promise<boolean> {
    this.loadingSpinner = true;
    return await this.updateContainerReuseStateToApprovedServiceCall(reuseNo);
  }

  public async updateContainerReuseStateToApprovedServiceCall(reuseNo: string): Promise<boolean> {
    const planningBlockIds = this.resolveCurrentPlanningBlockIds();
    try {
      const response = await this.sharedService.approveContainerReuseRequest(reuseNo).toPromise();
      this.ensureReuseResponseSuccess(response, "TOASTR_MESSAGES.ERROR.CONTAINER_REUSE_REQUEST_APPROVE_APPROVE_FAILED");
      const postApproveSelection = await this.resolvePostApproveSelectionPlanningBlocksSafely();
      await this.refreshPlanningBlocksForReuse(reuseNo, planningBlockIds);
      const res = await this.translate.get("TOASTR_MESSAGES.SUCCESS.CONTAINER_REUSE_ACCEPTED_COMPLETED_SUCCESSFULLY").toPromise();
      this.onSuccessEvents(res, false, postApproveSelection);
      return true;
    } catch (error) {
      await this.handleReuseActionError(error);
      return false;
    } finally {
      this.loadingSpinner = false;
    }
  }

  public async requestContainerReuse(): Promise<boolean> {
    this.loadingSpinner = true;
    return this.registerContainerReuseServiceCall();
  }

  public async requestAndApproveContainerReuse(): Promise<boolean> {
    this.loadingSpinner = true;
    return this.requestAndApproveContainerReuseServiceCall();
  }

  // One call: Business Central requests and approves together, and a refused approval
  // normally rolls the request back too, so nothing is left On Hold (G4L-26-658). See
  // showReuseRequestLeftAfterFailedApprove for the cases that do leave a request.
  public async requestAndApproveContainerReuseServiceCall(): Promise<boolean> {
    const planningBlockIds = this.resolveCurrentPlanningBlockIds();
    let requestSent = false;
    let approved = false;
    try {
      if (!this.hasValidImportExportPair()) {
        await this.handleReuseActionError(undefined);
        return false;
      }

      requestSent = true;
      const approveResponse = await this.sharedService.requestAndApproveContainerReuse(this.buildPreviewRequest()).toPromise();
      this.ensureReuseResponseSuccess(approveResponse, "TOASTR_MESSAGES.ERROR.CONTAINER_REUSE_REQUEST_APPROVE_APPROVE_FAILED");
      approved = true;
      const reuseNo = approveResponse.reuseNo || "";
      this.reuseNumberDisplay = reuseNo;

      const postApproveSelection = await this.resolvePostApproveSelectionPlanningBlocksSafely();
      await this.refreshPlanningBlocksForReuse(reuseNo);
      const res = await this.translate.get("TOASTR_MESSAGES.SUCCESS.CONTAINER_REUSE_ACCEPTED_COMPLETED_SUCCESSFULLY").toPromise();
      this.onSuccessEvents(res, false, postApproveSelection);
      return true;
    } catch (error) {
      if (error?.code === "REQUEST_APPROVE_RESOLVE_FAILED") {
        this.userFeedbackService.showErrorMessage("TOASTR_MESSAGES.ERROR.CONTAINER_REUSE_REQUEST_APPROVE_RESOLVE_FAILED");
      } else {
        await this.handleReuseActionError(error);
      }
      // Once Business Central answered with an approved reuse, a later error comes from
      // the steps after it (the planning block refresh, the success message): the reuse
      // is done and there is no request left to pick up.
      if (requestSent && !approved) {
        await this.showReuseRequestLeftAfterFailedApprove(planningBlockIds);
      }
      return false;
    } finally {
      this.loadingSpinner = false;
    }
  }

  // A failed request and approve can still leave a reuse request on the planning blocks,
  // for example against an older Business Central (the request was made, the separate
  // approve then refused). When every selected block now carries the same request and it
  // is still Requested, show it, as reopening the modal would, so the planner can approve
  // or reject it instead of it staying On Hold out of sight. It may predate this click.
  // A request carried by only one of the blocks is left alone: a request marks every
  // planning block it holds, so this one holds a block the modal does not show, and
  // approving it here would reuse that block.
  // Any other status is left alone too, so the error stays in view. An Approved request
  // here is an approve that committed part of its work and then failed, or one that
  // completed behind a timeout; showing it would replace the error with the
  // successful-reuse banner either way.
  private async showReuseRequestLeftAfterFailedApprove(planningBlockIds: number[]): Promise<void> {
    try {
      const planningBlocks = await this.sharedService.getPlanningBlocksByIds(planningBlockIds).toPromise() || [];
      const reuseNo = planningBlocks[0]?.containerReuseNo || "";
      const linkedPlanningBlockIds = planningBlocks
        .filter((planningBlock) => planningBlock.containerReuseNo === reuseNo)
        .map((planningBlock) => Number(planningBlock.id));
      if (!reuseNo || !planningBlockIds.length
        || !planningBlockIds.every((planningBlockId) => linkedPlanningBlockIds.includes(Number(planningBlockId)))) {
        return;
      }

      const containerReuseInfo = await this.sharedService.getContainerReuseRequestInfo(reuseNo).toPromise();
      if (!containerReuseInfo
        || this.resolveContainerReuseInfoStatus(containerReuseInfo) !== ContainerReuseStatus.REQUESTED) {
        return;
      }

      this.reuseNumberDisplay = reuseNo;
      try {
        await this.handlePlanningBlocksChanged(planningBlocks);
      } finally {
        // Also when the grid refresh failed, so the request shown can be acted on. Not
        // before the refresh: Approve would be enabled while it is still running.
        this.applyContainerReuseInfo(containerReuseInfo);
      }
    } catch {
      // The error already shown stays the planner's answer.
    }
  }

  public async registerContainerReuse(): Promise<RequestContainerReuseResponse> {
    if (!this.hasValidImportExportPair()) {
      return Promise.reject();
    }

    const response = await this.sharedService.requestContainerReuse(this.buildPreviewRequest()).toPromise();

    return this.ensureReuseResponseSuccess(response, "TOASTR_MESSAGES.ERROR.CONTAINER_REUSE_REQUEST_APPROVE_RESOLVE_FAILED");
  }

  public async registerContainerReuseServiceCall(): Promise<boolean> {
    try {
      const response = await this.registerContainerReuse();
      this.reuseNumberDisplay = response.reuseNo || this.reuseNumberDisplay;
      await this.refreshPlanningBlocksForReuse(this.reuseNumberDisplay);
      const res = await this.translate.get("TOASTR_MESSAGES.SUCCESS.CONTAINER_REUSE_REGISTRATION_COMPLETED_SUCCESSFULLY").toPromise();
      this.onSuccessEvents(res);
      return true;
    } catch (error) {
      await this.handleReuseActionError(error);
      return false;
    } finally {
      this.loadingSpinner = false;
    }
  }

  public onSuccessEvents(successMessage: string, keepModalOpen: boolean = false, selectPlanningBlocks: PlanningBlock[] = []): void {
    this.userFeedbackService.showSuccessMessageWithoutTranslating(successMessage);
    if (selectPlanningBlocks.length) {
      this.sharedActions.setSelectedPlanningBlocksForPbScreen(selectPlanningBlocks);
    } else {
      this.tableStateService.resetTableSelection(true);
    }

    if (!keepModalOpen) {
      this.resetModalState();
      this.sharedActions.setUiState(true);
    }
  }

  public onErrorEvents(): void {
    this.loadingSpinner = false;
    this.sharedActions.setUiState(true);
  }

  private normalizePlanningBlocks(planningBlocks: PlanningBlock[]): PlanningBlock[] {
    return (planningBlocks || []).map(planningBlock => ({
      ...planningBlock,
      containerOrTrailerType: planningBlock.containerOrTrailerType || planningBlock.containerType
    }));
  }

  private classifyPlanningBlocks(): void {
    this.importPlanningBlocks = this.selectedPlanningBlocks.filter(planningBlock => planningBlock.tractionOrder === 1);
    this.exportPlanningBlocks = this.selectedPlanningBlocks.filter(planningBlock => planningBlock.tractionOrder === 2);
    this.invalidPlanningBlocks = this.selectedPlanningBlocks.filter(planningBlock => planningBlock.tractionOrder !== 1 && planningBlock.tractionOrder !== 2);

    this.sortedPlanningBlocks = [...this.selectedPlanningBlocks].sort((leftPlanningBlock, rightPlanningBlock) => {
      return leftPlanningBlock.tractionOrder - rightPlanningBlock.tractionOrder;
    });
    this.registerPlanningBlocksForStatus(this.selectedPlanningBlocks);
    this.isReused = this.selectedPlanningBlocks.some((planningBlock) =>
      this.hasReusedPlanningBlockStatus(planningBlock));
  }

  private loadPlanningBlocksByReuseNumber(): void {
    this.loadingSpinner = true;
    this.planningBlocksLoadedByReuseNumber = true;
    this.sharedService.getPlanningBlockByReuseNo(this.reuseNumber)
      .subscribe((planningBlocks: PlanningBlock[]) => {
        this.selectedPlanningBlocks = this.normalizePlanningBlocks(planningBlocks || []);
        this.reuseNumberDisplay = this.reuseNumber;
        this.classifyPlanningBlocks();
        this.validateInput();
        this.loadContainerReuseInfo(this.reuseNumber);
        this.loadReusePreview();
        this.loadingSpinner = false;
      }, () => {
        this.selectedPlanningBlocks = [];
        this.classifyPlanningBlocks();
        this.showSelectionErrorInOrderSections = false;
        this.validationErrorMessage = this.translateValidation("INVALID_REUSE_NUMBER");
        this.resetPreviewState();
        this.loadingSpinner = false;
      });
  }

  private loadContainerReuseInfo(reuseNo: string): void {
    if (!reuseNo) {
      this.containerReuseInfo = null;
      this.containerReuseInfoStatusText = "";
      this.selectedContainerReuseInfoStatus = ContainerReuseStatus.REQUESTED;
      return;
    }

    this.sharedService.getContainerReuseRequestInfo(reuseNo)
      .subscribe((containerReuseInfo: ContainerReuseInfo) => {
        if (containerReuseInfo) {
          this.applyContainerReuseInfo(containerReuseInfo);
        } else {
          this.containerReuseInfo = null;
          this.containerReuseInfoStatusText = "";
          this.selectedContainerReuseInfoStatus = ContainerReuseStatus.REQUESTED;
        }
      }, () => {
        this.containerReuseInfo = null;
        this.containerReuseInfoStatusText = "";
        this.selectedContainerReuseInfoStatus = ContainerReuseStatus.REQUESTED;
      });
  }

  private applyContainerReuseInfo(containerReuseInfo: ContainerReuseInfo): void {
    this.containerReuseInfo = containerReuseInfo;
    this.containerReuseInfoStatus = this.resolveContainerReuseInfoStatus(containerReuseInfo);
    this.translateContainerReuseInfoStatusToText();
    this.selectedContainerReuseInfoStatus = this.resolveNextContainerReuseStatus();
  }

  private resolveContainerReuseInfoStatus(containerReuseInfo: ContainerReuseInfo): number {
    const textStatus = (containerReuseInfo.reuseRequestStatus || "").trim().toUpperCase();
    switch (textStatus) {
      case "APPROVED":
      case "COMPLETED":
        return ContainerReuseStatus.APPROVED;
      case "REJECTED":
        return ContainerReuseStatus.REJECTED;
      case "CANCELLED":
      case "CANCELED":
        return ContainerReuseStatus.CANCELED;
      case "OPEN":
      case "REQUESTED":
        return ContainerReuseStatus.REQUESTED;
    }

    if (containerReuseInfo.requestStatus !== undefined && containerReuseInfo.requestStatus !== null) {
      return containerReuseInfo.requestStatus;
    }

    return ContainerReuseStatus.REQUESTED;
  }

  private resolveNextContainerReuseStatus(): number {
    if (this.containerReuseInfoStatus === ContainerReuseStatus.REQUESTED) {
      return ContainerReuseStatus.APPROVED;
    }

    return this.containerReuseInfoStatus;
  }

  private validateInput(): void {
    this.showSelectionErrorInOrderSections = false;
    const hasReuseNumber = !!this.reuseNumber;
    const hasPlanningBlockInput = this.selectedPlanningBlocks.length > 0;

    if (!hasReuseNumber && !hasPlanningBlockInput) {
      this.validationErrorMessage = this.translateValidation("INVALID_INPUT");
      return;
    }

    if (hasReuseNumber && this.planningBlocksLoadedByReuseNumber && !hasPlanningBlockInput) {
      this.validationErrorMessage = this.translateValidation("NO_PLANNING_BLOCKS_FOR_REUSE_NUMBER");
      return;
    }

    if (hasPlanningBlockInput && this.selectedPlanningBlocks.length !== 2) {
      this.showSelectionErrorInOrderSections = true;
      this.validationErrorMessage = this.translateValidation("INVALID_SELECTION_COUNT");
      return;
    }

    if (hasPlanningBlockInput && (this.importPlanningBlocks.length !== 1 || this.exportPlanningBlocks.length !== 1 || this.invalidPlanningBlocks.length)) {
      this.showSelectionErrorInOrderSections = true;
      this.validationErrorMessage = this.translateValidation("INVALID_IMPORT_EXPORT_SELECTION");
      return;
    }

    if (hasPlanningBlockInput && this.hasDuplicatePlanningBlockSelection()) {
      this.validationErrorMessage = this.translateValidation("DUPLICATE_PLANNING_BLOCK_SELECTION");
      return;
    }

    if (hasPlanningBlockInput && this.hasSameOrderSelection()) {
      this.validationErrorMessage = this.translateValidation("SAME_ORDER_SELECTION");
      return;
    }

    this.validationErrorMessage = "";
    this.validatePlanningBlockContent();
  }

  private validatePlanningBlockContent(): void {
    if (!this.hasValidImportExportPair()) {
      this.validationWarningMessage = "";
      return;
    }

    const mismatches: string[] = [];

    if (this.isCarrierNoMismatch()) {
      mismatches.push(this.translateValidation("CARRIER_NO"));
    }

    if (this.isContainerOrTrailerTypeMismatch()) {
      mismatches.push(this.translateValidation("CONTAINER_OR_TRAILER_TYPE"));
    }

    this.validationWarningMessage = mismatches.length
      ? this.translateValidation("WARNING_DIFFERENT_VALUES", {
        values: mismatches.join(this.translateValidation("WARNING_LIST_SEPARATOR")),
      })
      : "";
  }

  private translateValidation(key: string, params?: any): string {
    return this.translate.instant(`CONTAINER_REUSE.VALIDATION.${key}`, params);
  }

  private hasValidImportExportPair(): boolean {
    return this.importPlanningBlocks.length === 1 && this.exportPlanningBlocks.length === 1;
  }

  private hasValidOrderSectionSelection(): boolean {
    return this.selectedPlanningBlocks.length === 2
      && this.invalidPlanningBlocks.length === 0
      && this.hasValidImportExportPair();
  }

  private hasMatchedPreview(): boolean {
    return !!this.previewResponse?.matchedScenario
      && !!this.previewResponse.result?.length
      && !this.previewErrorMessage;
  }

  private resolveReuseActionDisabledTooltipKey(status: number): string {
    if (this.loadingSpinner) {
      return "CONTAINER_REUSE.ACTION_DISABLED.LOADING";
    }

    switch (status) {
      case ContainerReuseStatus.REQUESTED:
      case ContainerReuseStatus.REQUEST_AND_APPROVE:
        if (this.hasExistingReuseRequest()) {
          return "CONTAINER_REUSE.ACTION_DISABLED.REUSE_REQUEST_EXISTS";
        }

        if (!!this.validationErrorMessage || !!this.previewErrorMessage || !this.hasValidImportExportPair() || !this.hasMatchedPreview()) {
          return "CONTAINER_REUSE.ACTION_DISABLED.PREVIEW_NOT_READY";
        }

        return "";
      case ContainerReuseStatus.APPROVED:
      case ContainerReuseStatus.REJECTED:
        if (!this.hasExistingReuseRequest() || !this.hasReuseNumberAvailable()) {
          return "CONTAINER_REUSE.ACTION_DISABLED.NO_REUSE_REQUEST";
        }

        if (this.isReused) {
          return "CONTAINER_REUSE.ACTION_DISABLED.REQUEST_ALREADY_APPROVED";
        }

        if (!this.isReuseRequestRequested()) {
          return "CONTAINER_REUSE.ACTION_DISABLED.REQUEST_NOT_REQUESTED";
        }

        return "";
      default:
        return "CONTAINER_REUSE.ACTION_DISABLED.PREVIEW_NOT_READY";
    }
  }

  private hasExistingReuseRequest(): boolean {
    return !!this.containerReuseInfo
      || this.hasReuseNumberAvailable()
      || (this.selectedPlanningBlocks || []).some((planningBlock) => !!planningBlock.containerReuseNo);
  }

  private isReuseRequestRequested(): boolean {
    return this.containerReuseInfoStatus === ContainerReuseStatus.REQUESTED;
  }

  private hasDuplicatePlanningBlockSelection(): boolean {
    const ids = this.selectedPlanningBlocks.map((planningBlock) => planningBlock.id).filter(Boolean);
    return ids.length !== _.uniq(ids).length;
  }

  private hasSameOrderSelection(): boolean {
    if (!this.hasValidImportExportPair()) {
      return false;
    }

    const importOrderNo = this.importPlanningBlocks[0]?.orderNumber?.toString();
    const exportOrderNo = this.exportPlanningBlocks[0]?.orderNumber?.toString();
    return !!importOrderNo && !!exportOrderNo && importOrderNo === exportOrderNo;
  }

  private hasReuseNumberAvailable(): boolean {
    return !!this.reuseNumberDisplay;
  }

  private resolveReuseNumberFromPlanningBlocks(): string {
    const reuseNumbers = _.uniq(this.selectedPlanningBlocks
      .map((planningBlock) => planningBlock.containerReuseNo)
      .filter(Boolean));

    return reuseNumbers.length === 1 ? reuseNumbers[0] : "";
  }

  private loadReusePreview(): void {
    this.resetPreviewState();

    if (!!this.validationErrorMessage || !this.hasValidImportExportPair() || this.hasSameOrderSelection()) {
      return;
    }

    const importOrderNumber = this.importPlanningBlocks[0]?.orderNumber;
    const exportOrderNumber = this.exportPlanningBlocks[0]?.orderNumber;

    if (!importOrderNumber || !exportOrderNumber) {
      this.previewErrorMessage = this.translateValidation("MISSING_ORDER_NUMBER");
      return;
    }

    this.loadingSpinner = true;

    Promise.all([
      this.sharedService.getOrderLinesByOrderNumber(importOrderNumber).toPromise(),
      this.sharedService.getOrderLinesByOrderNumber(exportOrderNumber).toPromise(),
    ]).then(async ([importOrderLines, exportOrderLines]) => {
      await this.loadPlanningBlockStatuses([...(importOrderLines || []), ...(exportOrderLines || [])]);
      this.importOrderLines = this.createPreviewOrderLineRows(importOrderNumber.toString(), importOrderLines || []);
      this.exportOrderLines = this.createPreviewOrderLineRows(exportOrderNumber.toString(), exportOrderLines || []);

      if (!this.importOrderLines.length || !this.exportOrderLines.length) {
        this.previewErrorMessage = this.translateValidation("NO_PREVIEW_ORDER_LINES");
        return Promise.resolve(null);
      }

      if (this.hasSuccessfulReuse()) {
        return Promise.resolve(null);
      }

      const payload = this.buildPreviewRequest();
      this.logPreviewRequest(payload);
      return this.sharedService.previewContainerReuse(payload).toPromise();
    }).then(async (previewResponse: ContainerReusePreviewResponse) => {
      if (!previewResponse) {
        return;
      }

      this.previewLoaded = true;
      this.previewResponse = previewResponse;

      if (!previewResponse.matchedScenario) {
        this.previewResultRows = [];
        this.previewErrorMessage = await this.extractReuseErrorMessage({
          message: previewResponse.message || previewResponse.error,
        });
      } else if (!previewResponse.result?.length) {
        this.previewResultRows = [];
        this.previewErrorMessage = this.translateValidation("EMPTY_PREVIEW_RESULT");
      } else {
        this.previewResultRows = this.createPreviewResultRows(previewResponse.result);
      }
    }).catch(async (error) => {
      this.previewErrorMessage = await this.extractReuseErrorMessage(error);
    }).finally(() => {
      this.loadingSpinner = false;
    });
  }

  private buildPreviewRequest(): ContainerReusePreviewRequest {
    return {
      importOrder: this.buildPreviewOrder(this.importPlanningBlocks[0].orderNumber.toString(), this.importOrderLines, true),
      exportOrder: this.buildPreviewOrder(this.exportPlanningBlocks[0].orderNumber.toString(), this.exportOrderLines, false),
    };
  }

  private logPreviewRequest(payload: ContainerReusePreviewRequest): void {
    // TODO: Remove this temporary manual logging once preview endpoint implementation testing no longer needs copied payloads.
    // tslint:disable-next-line:no-console
    console.log("[ContainerReuseNextModal] container/previewReuse payload", JSON.stringify(payload, null, 2));
  }

  private buildPreviewOrder(orderNo: string, rows: ReusePreviewOrderLineRow[], useLastPlanningBlock: boolean): ContainerReusePreviewOrder {
    const planningBlockId = this.resolveReusePlanningBlockId(rows, useLastPlanningBlock);

    if (!planningBlockId) {
      throw {
        success: false,
        error: "MISSING_REUSE_PLANNING_BLOCK",
        message: this.translateValidation("MISSING_PLANNING_BLOCK_ID"),
      };
    }

    const planningBlockRows = (rows || [])
      .filter((row) => row.planningBlockId === planningBlockId)
      .sort((first, second) => (first.sortingKey || 0) - (second.sortingKey || 0) || first.lineSequence - second.lineSequence);

    return {
      orderNo,
      planningBlocks: [{
        pbSequence: 1,
        orderLines: planningBlockRows.map((row, lineIndex): ContainerReusePreviewOrderLine => ({
          orderLineId: row.orderLineId,
          lineSequence: lineIndex + 1,
          actionCode: row.actionCode,
          addressNo: row.addressNo,
          planningBlockId: row.planningBlockId,
          sortingKey: row.sortingKey,
        })),
      }],
    };
  }

  private createPreviewOrderLineRows(orderNo: string, orderLines: OrderLine[]): ReusePreviewOrderLineRow[] {
    return [...(orderLines || [])]
      .sort((first, second) => (first.sortingKey || 0) - (second.sortingKey || 0))
      .map((orderLine, index) => {
        const addressNo = orderLine.addressNo || "";
        const address = orderLine.addressSearchName || orderLine.addressName || "";

        return {
          orderNo,
          orderLineId: this.resolveOrderLineId(orderLine),
          lineSequence: index + 1,
          actionCode: orderLine.actionNumber || "",
          status: this.resolveOrderLineStatus(orderLine),
          planningBlockStatusType: this.resolvePlanningBlockStatusType(orderLine.planningBlockId || 0),
          addressNo,
          address,
          displayAddress: this.formatAddressWithNumber(address, addressNo),
          planningBlockId: orderLine.planningBlockId || 0,
          sortingKey: orderLine.sortingKey || 0,
        };
      });
  }

  private resolveOrderLineStatus(orderLine: OrderLine): string {
    return orderLine.lineStatus?.name || orderLine.lineStatus?.code || "";
  }

  private resolveReusePlanningBlockId(rows: ReusePreviewOrderLineRow[], useLastPlanningBlock: boolean): number {
    const planningBlockIds = _.uniq((rows || [])
      .map((row) => row.planningBlockId)
      .filter((planningBlockId) => !!planningBlockId));

    if (!planningBlockIds.length) {
      return 0;
    }

    return useLastPlanningBlock
      ? planningBlockIds[planningBlockIds.length - 1]
      : planningBlockIds[0];
  }

  private createPreviewResultRows(resultLines: ContainerReusePreviewResultLine[]): ReusePreviewResultRow[] {
    const sourceLineByPlanningBlockAndOrderLine = new Map<string, ReusePreviewOrderLineRow>();
    const sourceLineByOrderAndOrderLine = new Map<string, ReusePreviewOrderLineRow>();
    const addressNameByAddressNo = new Map<string, string>();
    [...this.importOrderLines, ...this.exportOrderLines].forEach((row) => {
      sourceLineByPlanningBlockAndOrderLine.set(this.buildResultSourceKey(row.planningBlockId, row.orderLineId), row);
      sourceLineByOrderAndOrderLine.set(this.buildResultOrderSourceKey(row.orderNo, row.orderLineId), row);
      if (row.addressNo && row.address && !addressNameByAddressNo.has(row.addressNo)) {
        addressNameByAddressNo.set(row.addressNo, row.address);
      }
    });

    const importOrderNo = this.importPlanningBlocks[0]?.orderNumber?.toString() || this.importOrderLines[0]?.orderNo || "";
    const exportOrderNo = this.exportPlanningBlocks[0]?.orderNumber?.toString() || this.exportOrderLines[0]?.orderNo || "";
    const orderedResultLines = [...(resultLines || [])]
      .sort((first, second) => first.pbSequence - second.pbSequence || first.lineSequence - second.lineSequence);
    const resultOrderByPlanningBlockSequence = this.resolveResultOrderByPlanningBlockSequence(
      orderedResultLines,
      importOrderNo,
      exportOrderNo,
      sourceLineByPlanningBlockAndOrderLine,
      sourceLineByOrderAndOrderLine
    );
    const assignedResultLines = orderedResultLines.map((resultLine) => {
      const sourceLine = this.resolveResultSourceLine(resultLine, sourceLineByPlanningBlockAndOrderLine, sourceLineByOrderAndOrderLine);
      const orderNo = this.resolveKnownPreviewOrderNo(resultLine.orderNo, importOrderNo, exportOrderNo)
        || this.resolveKnownPreviewOrderNo(sourceLine?.orderNo, importOrderNo, exportOrderNo)
        || resultOrderByPlanningBlockSequence.get(resultLine.pbSequence)
        || "";

      if (!orderNo) {
        throw {
          success: false,
          error: "PREVIEW_ORDER_MAPPING_FAILED",
          message: this.translateValidation("PREVIEW_ORDER_MAPPING_FAILED"),
        };
      }

      return { ...resultLine, orderNo };
    });
    const resultOrderLineIds = new Set(assignedResultLines
      .filter((resultLine) => !!resultLine.orderLineId)
      .map((resultLine) => resultLine.orderLineId));
    const importReplacementPlanningBlockId = this.resolveReusePlanningBlockId(this.importOrderLines, true);
    const exportReplacementPlanningBlockId = this.resolveReusePlanningBlockId(this.exportOrderLines, false);
    const unchangedImportRows = this.createUnchangedPreviewResultRows(
      this.importOrderLines,
      importReplacementPlanningBlockId,
      resultOrderLineIds
    );
    const unchangedExportRows = this.createUnchangedPreviewResultRows(
      this.exportOrderLines,
      exportReplacementPlanningBlockId,
      resultOrderLineIds
    );
    const importResultRows = this.createBusinessCentralPreviewResultRows(
      assignedResultLines.filter((resultLine) => resultLine.orderNo === importOrderNo),
      sourceLineByPlanningBlockAndOrderLine,
      sourceLineByOrderAndOrderLine,
      addressNameByAddressNo
    );
    const exportResultRows = this.createBusinessCentralPreviewResultRows(
      assignedResultLines.filter((resultLine) => resultLine.orderNo === exportOrderNo),
      sourceLineByPlanningBlockAndOrderLine,
      sourceLineByOrderAndOrderLine,
      addressNameByAddressNo
    );

    const previewRows = [
      ...unchangedImportRows,
      ...importResultRows,
      ...exportResultRows,
      ...unchangedExportRows,
    ];

    return previewRows.map((row, index) => ({
      ...row,
      startsPlanningBlock: index > 0
        && this.resolvePreviewPlanningBlockKey(row) !== this.resolvePreviewPlanningBlockKey(previewRows[index - 1]),
    }));
  }

  private resolvePreviewPlanningBlockKey(row: ReusePreviewResultRow): string {
    const planningBlockSegment = row.isNewPlanningBlockLine ? "new" : row.planningBlockId;
    return `${row.orderNo}|${row.pbSequence}|${planningBlockSegment}`;
  }

  private resolveResultOrderByPlanningBlockSequence(
    resultLines: ContainerReusePreviewResultLine[],
    importOrderNo: string,
    exportOrderNo: string,
    sourceLineByPlanningBlockAndOrderLine: Map<string, ReusePreviewOrderLineRow>,
    sourceLineByOrderAndOrderLine: Map<string, ReusePreviewOrderLineRow>
  ): Map<number, string> {
    const orderBySequence = new Map<number, string>();
    const ambiguousSequences = new Set<number>();

    resultLines.forEach((resultLine) => {
      if (ambiguousSequences.has(resultLine.pbSequence)) {
        return;
      }

      const sourceLine = this.resolveResultSourceLine(resultLine, sourceLineByPlanningBlockAndOrderLine, sourceLineByOrderAndOrderLine);
      const orderNo = this.resolveKnownPreviewOrderNo(resultLine.orderNo, importOrderNo, exportOrderNo)
        || this.resolveKnownPreviewOrderNo(sourceLine?.orderNo, importOrderNo, exportOrderNo);

      if (!orderNo) {
        return;
      }

      const existingOrderNo = orderBySequence.get(resultLine.pbSequence);
      if (existingOrderNo && existingOrderNo !== orderNo) {
        orderBySequence.delete(resultLine.pbSequence);
        ambiguousSequences.add(resultLine.pbSequence);
        return;
      }

      orderBySequence.set(resultLine.pbSequence, orderNo);
    });

    return orderBySequence;
  }

  private resolveKnownPreviewOrderNo(orderNo: string, importOrderNo: string, exportOrderNo: string): string {
    const candidate = (orderNo || "").toString();
    return candidate === importOrderNo || candidate === exportOrderNo ? candidate : "";
  }

  private createUnchangedPreviewResultRows(
    sourceRows: ReusePreviewOrderLineRow[],
    replacedPlanningBlockId: number,
    resultOrderLineIds: Set<string>
  ): ReusePreviewResultRow[] {
    const unchangedRows = (sourceRows || []).filter((row) =>
      row.planningBlockId !== replacedPlanningBlockId
      && !resultOrderLineIds.has(row.orderLineId));
    const planningBlockIds = _.uniq(unchangedRows.map((row) => row.planningBlockId));

    return _.flatMap(planningBlockIds, (planningBlockId, planningBlockIndex) => unchangedRows
      .filter((row) => row.planningBlockId === planningBlockId)
      .sort((first, second) => (first.sortingKey || 0) - (second.sortingKey || 0) || first.lineSequence - second.lineSequence)
      .map((row, lineIndex): ReusePreviewResultRow => ({
        orderLineId: row.orderLineId,
        orderNo: row.orderNo,
        pbSequence: planningBlockIndex + 1,
        lineSequence: lineIndex + 1,
        planningBlockId: row.planningBlockId,
        actionCode: row.actionCode,
        addressNo: row.addressNo,
        reuse: false,
        splitPB: false,
        newLine: false,
        iawInsertBefore: false,
        address: row.address,
        displayLineSequence: lineIndex + 1,
        displayAddress: row.address || row.addressNo,
        displayAddressWithNumber: row.displayAddress,
        displayOrderLineId: row.orderLineId,
        displayPlanningBlockId: row.planningBlockId ? row.planningBlockId.toString() : "",
        isNewPlanningBlockLine: false,
        planningBlockStatusType: row.planningBlockStatusType
          ?? this.resolvePlanningBlockStatusType(row.planningBlockId, false, false, true),
        startsPlanningBlock: false,
      })));
  }

  private createBusinessCentralPreviewResultRows(
    resultLines: ContainerReusePreviewResultLine[],
    sourceLineByPlanningBlockAndOrderLine: Map<string, ReusePreviewOrderLineRow>,
    sourceLineByOrderAndOrderLine: Map<string, ReusePreviewOrderLineRow>,
    addressNameByAddressNo: Map<string, string>
  ): ReusePreviewResultRow[] {
    let newPlanningBlockSequence: number | null = null;

    return [...(resultLines || [])]
      .sort((first, second) =>
        first.pbSequence - second.pbSequence
        || this.resolveResultLineSortSequence(first, sourceLineByPlanningBlockAndOrderLine, sourceLineByOrderAndOrderLine)
        - this.resolveResultLineSortSequence(second, sourceLineByPlanningBlockAndOrderLine, sourceLineByOrderAndOrderLine))
      .map((resultLine) => {
        if (newPlanningBlockSequence !== null && resultLine.pbSequence !== newPlanningBlockSequence) {
          newPlanningBlockSequence = null;
        }

        const sourceLine = this.resolveResultSourceLine(resultLine, sourceLineByPlanningBlockAndOrderLine, sourceLineByOrderAndOrderLine);
        const addressNo = sourceLine?.addressNo || resultLine.addressNo || "";
        const displayAddress = sourceLine?.address || addressNameByAddressNo.get(addressNo) || addressNo;
        const planningBlockId = resultLine.planningBlockId || 0;
        const isNewPlanningBlockLine = resultLine.splitPB || resultLine.pbSequence === newPlanningBlockSequence;

        if (resultLine.splitPB) {
          newPlanningBlockSequence = resultLine.pbSequence;
        }

        return {
          ...resultLine,
          orderNo: resultLine.orderNo || sourceLine?.orderNo || "",
          addressNo,
          address: sourceLine?.address || "",
          displayLineSequence: this.resolveDisplayLineSequence(resultLine, sourceLine),
          displayAddress,
          displayAddressWithNumber: this.formatAddressWithNumber(displayAddress, addressNo),
          displayOrderLineId: resultLine.newLine || !resultLine.orderLineId ? "new orderline" : resultLine.orderLineId,
          displayPlanningBlockId: this.formatDisplayPlanningBlockId(planningBlockId, isNewPlanningBlockLine),
          isNewPlanningBlockLine,
          planningBlockStatusType: this.resolvePlanningBlockStatusType(
            planningBlockId,
            resultLine.reuse,
            isNewPlanningBlockLine,
            true
          ),
          startsPlanningBlock: false,
        };
      });
  }

  private async loadPlanningBlockStatuses(orderLines: OrderLine[]): Promise<void> {
    const planningBlockIds = _.uniq((orderLines || [])
      .map((orderLine) => orderLine.planningBlockId)
      .filter((planningBlockId) => !!planningBlockId));

    if (!planningBlockIds.length) {
      return;
    }

    try {
      const planningBlocks = await this.sharedService.getPlanningBlocksByIds(planningBlockIds).toPromise();
      this.registerPlanningBlocksForStatus(planningBlocks || []);
    } catch {
      // Status enrichment must not block previewing or requesting reuse.
    }
  }

  private registerPlanningBlocksForStatus(planningBlocks: PlanningBlock[]): void {
    (planningBlocks || []).forEach((planningBlock) => {
      if (planningBlock?.id) {
        this.planningBlockById.set(planningBlock.id, planningBlock);
      }
    });
  }

  private hasReusedPlanningBlockStatus(planningBlock: PlanningBlock): boolean {
    return (planningBlock?.statusType ?? planningBlock?.status?.statusType) === RE_USED;
  }

  private resolvePlanningBlockStatusType(
    planningBlockId: number,
    reused: boolean = false,
    isNewPlanningBlockLine: boolean = false,
    useToBePlannedFallback: boolean = false
  ): number {
    if (reused) {
      return RE_USED;
    }

    if (isNewPlanningBlockLine) {
      return TO_BE_PLANNED;
    }

    const planningBlock = this.planningBlockById.get(planningBlockId);
    return planningBlock?.statusType
      ?? planningBlock?.status?.statusType
      ?? (useToBePlannedFallback ? TO_BE_PLANNED : null);
  }

  private formatAddressWithNumber(address: string, addressNo: string): string {
    const normalizedAddress = (address || "").trim();
    const normalizedAddressNo = (addressNo || "").trim();

    if (!normalizedAddressNo || normalizedAddress === normalizedAddressNo) {
      return normalizedAddress || normalizedAddressNo;
    }

    if (!normalizedAddress) {
      return normalizedAddressNo;
    }

    return `${normalizedAddress} (${normalizedAddressNo})`;
  }

  private resolveResultLineSortSequence(
    resultLine: ContainerReusePreviewResultLine,
    sourceLineByPlanningBlockAndOrderLine: Map<string, ReusePreviewOrderLineRow>,
    sourceLineByOrderAndOrderLine: Map<string, ReusePreviewOrderLineRow>
  ): number {
    const sourceLine = this.resolveResultSourceLine(resultLine, sourceLineByPlanningBlockAndOrderLine, sourceLineByOrderAndOrderLine);
    return this.resolveDisplayLineSequence(resultLine, sourceLine) || 0;
  }

  private resolveResultSourceLine(
    resultLine: ContainerReusePreviewResultLine,
    sourceLineByPlanningBlockAndOrderLine: Map<string, ReusePreviewOrderLineRow>,
    sourceLineByOrderAndOrderLine: Map<string, ReusePreviewOrderLineRow>
  ): ReusePreviewOrderLineRow {
    return sourceLineByPlanningBlockAndOrderLine.get(this.buildResultSourceKey(resultLine.planningBlockId, resultLine.orderLineId))
      || sourceLineByOrderAndOrderLine.get(this.buildResultOrderSourceKey(resultLine.orderNo, resultLine.orderLineId));
  }

  private resolveDisplayLineSequence(resultLine: ContainerReusePreviewResultLine, sourceLine?: ReusePreviewOrderLineRow): number | null {
    const businessCentralLineSequence = resultLine.lineSequence;

    if (businessCentralLineSequence !== null && businessCentralLineSequence !== undefined && businessCentralLineSequence !== 0) {
      return businessCentralLineSequence;
    }

    if (sourceLine?.lineSequence !== null && sourceLine?.lineSequence !== undefined) {
      return sourceLine.lineSequence;
    }

    return businessCentralLineSequence;
  }

  private formatDisplayPlanningBlockId(planningBlockId: number, isNewPlanningBlockLine: boolean): string {
    if (isNewPlanningBlockLine) {
      return "(New PB)";
    }

    return planningBlockId ? planningBlockId.toString() : "";
  }

  private buildResultSourceKey(planningBlockId: number, orderLineId: string): string {
    return `${planningBlockId || 0}|${orderLineId || ""}`;
  }

  private buildResultOrderSourceKey(orderNo: string, orderLineId: string): string {
    return `${orderNo || ""}|${orderLineId || ""}`;
  }

  public getPreviewScenarioDisplay(): string {
    if (!this.previewResponse) {
      return "";
    }

    return [this.previewResponse.description, this.previewResponse.addressVariant]
      .filter(Boolean)
      .join(" - ")
      || this.previewResponse.scenario
      || "";
  }

  private ensureReuseResponseSuccess<TResponse extends ContainerReuseBusinessResponse>(response: TResponse, fallbackMessageKey: string): TResponse {
    if (!response || response.success !== true) {
      throw response || {
        success: false,
        error: "EMPTY_RESPONSE",
        message: this.translate.instant(fallbackMessageKey),
      };
    }

    if (!response.reuseNo) {
      throw {
        success: false,
        error: "MISSING_REUSE_NO",
        message: this.translate.instant(fallbackMessageKey),
      };
    }

    return response;
  }

  private async refreshPlanningBlocksForReuse(reuseNo: string, fallbackPlanningBlockIds: number[] = []): Promise<void> {
    if (!reuseNo) {
      return;
    }

    const planningBlocks = await this.sharedService.getPlanningBlockByReuseNo(reuseNo).toPromise();
    if (planningBlocks?.length) {
      await this.handlePlanningBlocksChanged(planningBlocks);
      return;
    }

    if (fallbackPlanningBlockIds.length) {
      const fallbackPlanningBlocks = await this.sharedService.getPlanningBlocksByIds(fallbackPlanningBlockIds).toPromise();
      await this.handlePlanningBlocksChanged(fallbackPlanningBlocks || []);
    }
  }

  private resolveCurrentPlanningBlockIds(): number[] {
    return _.uniq((this.selectedPlanningBlocks || [])
      .map((planningBlock) => planningBlock.id)
      .filter((planningBlockId) => !!planningBlockId));
  }

  private async resolvePostApproveSelectionPlanningBlocksSafely(): Promise<PlanningBlock[]> {
    try {
      return await this.resolvePostApproveSelectionPlanningBlocks();
    } catch {
      return [];
    }
  }

  private async resolvePostApproveSelectionPlanningBlocks(): Promise<PlanningBlock[]> {
    const importOrderNo = this.importPlanningBlocks[0]?.orderNumber?.toString() || this.importOrderLines[0]?.orderNo || "";
    const exportOrderNo = this.exportPlanningBlocks[0]?.orderNumber?.toString() || this.exportOrderLines[0]?.orderNo || "";

    if (!importOrderNo || !exportOrderNo) {
      return [];
    }

    const [importOrderLines, exportOrderLines] = await Promise.all([
      this.sharedService.getOrderLinesByOrderNumber(importOrderNo).toPromise(),
      this.sharedService.getOrderLinesByOrderNumber(exportOrderNo).toPromise(),
    ]);

    const latestImportRows = this.createPreviewOrderLineRows(importOrderNo, importOrderLines || []);
    const latestExportRows = this.createPreviewOrderLineRows(exportOrderNo, exportOrderLines || []);
    const candidatePlanningBlockIds = _.uniq([
      ...this.resolvePostApproveImportPlanningBlockIds(latestImportRows),
      ...this.resolvePostApproveExportPlanningBlockIds(latestExportRows),
    ]);

    if (!candidatePlanningBlockIds.length) {
      return [];
    }

    const planningBlocks = await this.sharedService.getTmsPlanningBlocksByIds(candidatePlanningBlockIds).toPromise();
    const selectablePlanningBlocks = (planningBlocks || [])
      .filter((planningBlock) => planningBlock.statusType === TO_BE_PLANNED);

    if (selectablePlanningBlocks.length) {
      this.sharedActions.updatePlanningBlocksDisplay(selectablePlanningBlocks, true);
    }

    return selectablePlanningBlocks;
  }

  private resolvePostApproveImportPlanningBlockIds(rows: ReusePreviewOrderLineRow[]): number[] {
    const planningBlockIds = this.resolveDistinctOrderPlanningBlockIds(rows);
    return planningBlockIds.slice(0, Math.max(planningBlockIds.length - 1, 0));
  }

  private resolvePostApproveExportPlanningBlockIds(rows: ReusePreviewOrderLineRow[]): number[] {
    return this.resolveDistinctOrderPlanningBlockIds(rows).slice(1);
  }

  private resolveDistinctOrderPlanningBlockIds(rows: ReusePreviewOrderLineRow[]): number[] {
    return _.uniq((rows || [])
      .map((row) => row.planningBlockId)
      .filter((planningBlockId) => !!planningBlockId));
  }

  private async handleReuseActionError(error: any): Promise<void> {
    this.previewErrorMessage = await this.extractReuseErrorMessage(error);
    this.onErrorEvents();
  }

  private extractReuseErrorMessage(error: any): Promise<string> {
    return formatErrorMessage(error, this.translate.instant(UNEXPECTED_SERVER_ERROR_KEY));
  }

  private resolveOrderLineId(orderLine: OrderLine): string {
    return ((orderLine.orderLineId || orderLine.orderLineID || "") as any).toString();
  }

  private resetPreviewState(): void {
    this.importOrderLines = [];
    this.exportOrderLines = [];
    this.previewResultRows = [];
    this.previewResponse = null;
    this.previewErrorMessage = "";
    this.previewLoaded = false;
  }

  private async handlePlanningBlocksChanged(planningBlocks: PlanningBlock[]): Promise<void> {
    if (!planningBlocks) {
      return;
    }

    this.selectedPlanningBlocks = this.normalizePlanningBlocks(planningBlocks);
    this.classifyPlanningBlocks();
    this.validateInput();
    this.reuseNumberDisplay = this.resolveReuseNumberFromPlanningBlocks() || this.reuseNumberDisplay;
    this.sharedActions.updatePlanningBlocksDisplayCpl(planningBlocks);

    // The PB screen and planning requests need the TMS contract, including
    // structured remarks and dates; Container Planning returns display values.
    const planningBlockIds = _.uniq(planningBlocks.map((planningBlock) => planningBlock.id).filter((id) => !!id));
    if (planningBlockIds.length) {
      const tmsPlanningBlocks = await this.sharedService.getTmsPlanningBlocksByIds(planningBlockIds).toPromise();
      if (tmsPlanningBlocks?.length) {
        this.sharedActions.updatePlanningBlocksDisplay(tmsPlanningBlocks, true);
      }
    }
  }

  private resetModalState(): void {
    this.resetPreviewState();
    this.planningBlockById.clear();
    this.containerReuseInfo = null;
    this.containerReuseInfoStatusText = "";
    this.reuseNumberDisplay = "";
    this.selectedContainerReuseInfoStatus = ContainerReuseStatus.REQUESTED;
    this.isReused = false;
    this.showSelectionErrorInOrderSections = false;
  }
}
